// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Hearsay — the bootstrap. Owns the screens and the room, and no game logic whatsoever.
//
// The room is joined ONCE and held until the player goes back to the menu. Every hearing after the
// first happens inside it, versioned by rematch.ts. Leaving and re-joining to "reset" hands back the
// room object Trystero is still tearing down, and both players then sit in the right room code,
// alone, for ever.

import '@ben-gy/game-engine/mobile.css';
import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createNet, roomAppId, setTurnConfig, type Net, type PeerId } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type Rounds } from '@ben-gy/game-engine/rematch';
import {
  clearRoomInUrl, createLobby, createRoomEntry, normalizeRoomCode, setRoomInUrl, type Lobby,
} from '@ben-gy/game-engine/lobby';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { DEFAULT_MODE, MODES, MODE_IDS, matchOffset, modeOf, openingOrder, type ModeId } from './modes';
import type { Award, PrivateView, PublicState, SeatSpec } from './game';
import { CHANNELS, Session, type Action, type HandAttest, type Transport } from './session';
import { View, type Armed } from './view';
import { runCountdown, type Countdown } from './countdown';
import {
  FOOTER_HTML, HOW_HTML, aboutHtml, cardHtml, cardsHtml, matchOverHtml, menuHtml, openPanel, summaryHtml,
} from './ui';
import './styles/main.css';

const SLUG = 'hearsay';
const MIN_PLAYERS = 2;

const app = document.getElementById('app')!;
const store = createStore(SLUG);
const sfx = createSfx({
  muted: store.get<boolean>('muted', false) ?? false,
  patches: {
    tap: { type: 'square', freq: [520, 520], dur: 0.03, gain: 0.09 },
    deal: { type: 'triangle', freq: [340, 470], dur: 0.07, gain: 0.12 },
    call: { type: 'triangle', freq: [430, 720], dur: 0.11, gain: 0.17 },
    hit: { type: 'sawtooth', freq: [300, 90], dur: 0.24, gain: 0.26, noise: true },
    miss: { type: 'square', freq: [200, 140], dur: 0.12, gain: 0.14 },
    shield: { type: 'sine', freq: [520, 780], dur: 0.18, gain: 0.15 },
    swap: { type: 'triangle', freq: [600, 380], dur: 0.14, gain: 0.15 },
    nope: { type: 'square', freq: [190, 120], dur: 0.13, gain: 0.15 },
    tick: { type: 'triangle', freq: [660, 660], dur: 0.08, gain: 0.16 },
    go: { type: 'triangle', freq: [880, 1320], dur: 0.22, gain: 0.22 },
    over: { type: 'triangle', freq: [560, 320], dur: 0.5, gain: 0.2 },
  },
});
const play = (n: string): void => {
  try {
    sfx.play(n);
  } catch {
    /* audio is decoration; never let it take the game down */
  }
};

/** A distinct handle per device, so two peers are never both "You". */
const HANDLES = ['Rook', 'Marsh', 'Quill', 'Vetch', 'Brack', 'Slade', 'Thorn', 'Wick', 'Gale', 'Pike', 'Fen', 'Bram'];
function playerName(): string {
  const stored = store.get<string>('name', '') ?? '';
  if (stored) return stored;
  const gen = `${HANDLES[Math.floor(Math.random() * HANDLES.length)]}-${Math.floor(Math.random() * 90 + 10)}`;
  store.set('name', gen);
  return gen;
}

let mode: ModeId = modeOf(store.get<string>('mode', DEFAULT_MODE)).id;

let net: Net | null = null;
let rounds: Rounds<{ mode: string }> | null = null;
let lobby: Lobby | null = null;
let session: Session | null = null;
let view: View | null = null;
let countdown: Countdown | null = null;
let unwire: (() => void) | null = null;
let roomCode = '';
let iMintedIt = false;
let deepLinkUsed = false;
let armed: Armed = { card: null, target: null, named: null };
let flash = '';
let flashAt = 0;
let latestPub: PublicState | null = null;
let latestPriv: PrivateView | null = null;
let standing: Record<string, number> = {};
let matchSeed = 0;
let soloRound = 0;
let seenPly = 0;

const setPlaying = (on: boolean): void => {
  document.body.classList.toggle('playing', on);
};

function teardownGame(): void {
  countdown?.cancel();
  countdown = null;
  session?.destroy();
  session = null;
  view?.destroy();
  view = null;
  document.removeEventListener('keydown', onKey);
  latestPub = null;
  latestPriv = null;
  seenPly = 0;
  armed = { card: null, target: null, named: null };
}

function clearScreen(): void {
  teardownGame();
  app.replaceChildren();
}

function onKey(e: KeyboardEvent): void {
  if (!view) return;
  if ((e.target as HTMLElement)?.tagName === 'INPUT') return;
  if (view.handleKey(e)) e.preventDefault();
}

// ── screens ───────────────────────────────────────────────────────────────────────────────────

function showMenu(): void {
  clearScreen();
  setPlaying(false);
  standing = {};
  app.innerHTML = menuHtml(mode, sfx.muted(), store.get<number>(`won:${mode}`, 0) ?? 0);

  app.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((b) =>
    b.addEventListener('click', () => {
      mode = modeOf(b.dataset.mode).id;
      store.set('mode', mode);
      showMenu();
    }),
  );
  app.querySelector('#playSolo')!.addEventListener('click', () => {
    sfx.unlock();
    play('tap');
    startSolo();
  });
  app.querySelector('#playFriends')!.addEventListener('click', () => {
    sfx.unlock();
    play('tap');
    showRoomEntry();
  });
  app.querySelector('#howBtn')!.addEventListener('click', () => openPanel(app, HOW_HTML));
  app.querySelector('#cardsBtn')!.addEventListener('click', () => openPanel(app, cardsHtml()));
  app.querySelector('#aboutBtn')!.addEventListener('click', () => openPanel(app, aboutHtml()));
  const mute = app.querySelector<HTMLButtonElement>('#muteBtn')!;
  mute.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    mute.textContent = sfx.muted() ? 'Sound off' : 'Sound on';
    mute.setAttribute('aria-pressed', String(sfx.muted()));
  });

  if (!store.get<boolean>('seenHow', false)) {
    store.set('seenHow', true);
    openPanel(app, HOW_HTML);
  }
}

/** Solo is instant: three seats, no lobby, first card dealt inside a second. */
function startSolo(seats = 3): void {
  const m = MODES[mode];
  const players = Math.max(m.minPlayers, Math.min(seats, m.maxPlayers));
  matchSeed = (Math.random() * 0xffffffff) >>> 0;
  standing = {};
  soloRound = 0;
  // The local seat uses its real handle rather than "You": the table log is written in the third
  // person ("Marsh names the Witness"), and "You names the Witness" is what the alternative reads
  // like. The standing chip and the summary still say "You", which is where it belongs.
  const names = [playerName(), 'Marsh', 'Vetch', 'Brack', 'Thorn', 'Slade'].slice(0, players);
  const specs: SeatSpec[] = names.map((n, i) => ({ id: i === 0 ? 'me' : `bot${i}`, name: n, bot: i > 0 }));
  for (const s of specs) standing[s.id] = 0;
  nextSoloRound(specs);
}

function nextSoloRound(specs: SeatSpec[]): void {
  soloRound++;
  const order = openingOrder(specs, soloRound, matchOffset(matchSeed, specs.length));
  startRound({
    seed: matchSeed + soloRound * 7919,
    round: soloRound,
    specs: order,
    isHost: true,
    solo: true,
    onDone: () => nextSoloRound(specs),
  });
}

// ── the room ──────────────────────────────────────────────────────────────────────────────────

function showRoomEntry(): void {
  clearScreen();
  setPlaying(false);
  const host = document.createElement('div');
  host.className = 'main-content';
  app.append(host);
  createRoomEntry({
    container: host,
    title: 'Play with friends',
    subtitle: 'Start a room and send the link — or scan the code, or type it in.',
    onSubmit: (code, created) => {
      roomCode = normalizeRoomCode(code);
      iMintedIt = created;
      void enterRoom();
    },
    onCancel: () => showMenu(),
  });
  app.insertAdjacentHTML('beforeend', FOOTER_HTML);
}

async function enterRoom(): Promise<void> {
  clearScreen();
  setRoomInUrl(roomCode);
  standing = {};
  try {
    net = createNet(
      { appId: roomAppId(SLUG), roomId: roomCode, claimHost: iMintedIt },
      {
        // NEVER navigate from a net handler: these fire in the lobby AND just after a hearing ends.
        onPeers: () => {
          lobby?.repaint();
          session?.onRoster();
          syncAuthority();
        },
        onPeerLeave: (id: PeerId) => {
          session?.onPeerLeave(id);
          lobby?.repaint();
          syncAuthority();
        },
        onHostChange: () => {
          lobby?.repaint();
          syncAuthority();
        },
      },
    );
  } catch {
    showMenu();
    return;
  }

  rounds = createRounds<{ mode: string }>({
    net,
    playerName: playerName(),
    minPlayers: MIN_PLAYERS,
    roundOpts: () => ({ mode }),
    onRound: (info) => {
      // The HOST's pick is what the room plays, frozen inside the start.
      const m = modeOf(info.opts?.mode).id;
      if (!info.seated) {
        showSpectator();
        return;
      }
      if (info.round === 1) {
        standing = {};
        matchSeed = info.seed;
      }
      const cap = MODES[m].maxPlayers;
      const roster = info.players.slice(0, cap);
      for (const p of roster) if (standing[p.id] === undefined) standing[p.id] = 0;
      const specs: SeatSpec[] = roster.map((p) => ({ id: p.id, name: p.name, bot: false }));
      const order = openingOrder(specs, info.round, matchOffset(matchSeed, specs.length));
      mode = m;
      startRound({
        seed: info.seed,
        round: info.round,
        specs: order,
        isHost: info.isHost,
        solo: false,
        onDone: () => {},
      });
    },
    onChange: () => lobby?.repaint(),
  });

  showLobby();
}

function showLobby(): void {
  clearScreen();
  setPlaying(false);
  const host = document.createElement('div');
  host.className = 'main-content';
  app.append(host);
  lobby = createLobby({
    container: host,
    net: net!,
    rounds: rounds!,
    roomCode,
    minPlayers: MIN_PLAYERS,
    maxPlayers: MODES[mode].maxPlayers,
    modeSlot: () => {
      const st = rounds!.state();
      // Guests see the HOST's gossiped choice. Rendering your own pick and labelling it the host's
      // is a confident lie, and it has shipped in this fleet before.
      const chosen = st.isHost ? mode : modeOf(st.hostOpts?.mode).id;
      return `<div class="modeslot"><span class="modeslot-label">Mode</span>
        <div class="segmented small">${MODE_IDS.map(
          (id) =>
            `<button type="button" class="seg${id === chosen ? ' on' : ''}" data-lmode="${id}"${
              st.isHost ? '' : ' disabled'
            }>${MODES[id].name}</button>`,
        ).join('')}</div>
        <p class="fine">${st.isHost ? MODES[mode].blurb : 'The host picks the mode.'}</p></div>`;
    },
    onModeMount: () => {
      host.querySelectorAll<HTMLButtonElement>('[data-lmode]').forEach((b) =>
        b.addEventListener('click', () => {
          mode = modeOf(b.dataset.lmode).id;
          store.set('mode', mode);
          lobby?.repaint();
        }),
      );
    },
    onCancel: () => void leaveRoom(),
  });
  app.insertAdjacentHTML('beforeend', FOOTER_HTML);
}

function showSpectator(): void {
  clearScreen();
  setPlaying(false);
  app.innerHTML = `<div class="main-content"><h2>A hearing is already under way</h2>
    <p class="fine">You are in the next one. Hang on — it will start on its own.</p></div>${FOOTER_HTML}`;
}

/** Leaving is the ONLY place the room is torn down, and it clears `?room=` on the way out. */
async function leaveRoom(): Promise<void> {
  teardownGame();
  unwire?.();
  unwire = null;
  rounds?.destroy();
  rounds = null;
  lobby?.destroy();
  lobby = null;
  const n = net;
  net = null;
  clearRoomInUrl();
  deepLinkUsed = true;
  await n?.leave();
  showMenu();
}

function syncAuthority(): void {
  if (!net || !session) return;
  if (!net.hostSettled()) return;
  session.setHost(session.authorityFor(net.peers(), net.host()) === net.selfId);
}

function wireRound(n: Net): { transport: Transport; off: () => void } {
  const sendSnap = n.channel<PublicState>(CHANNELS.snap, (pub) => session?.onSnapshot(pub));
  const sendPriv = n.channel<PrivateView>(CHANNELS.priv, (p) => session?.onPrivate(p));
  const sendAct = n.channel<Action>(CHANNELS.act, (a, from) => session?.onAction(from, a));
  const sendReq = n.channel<null>(CHANNELS.handRequest, (_d, from) => session?.onHandRequest(from));
  const sendGive = n.channel<HandAttest>(CHANNELS.handReply, (m, from) => session?.onHandReply(from, m));
  return {
    transport: {
      sendSnap: (pub) => sendSnap(pub),
      sendPriv: (to, priv) => sendPriv(priv, to),
      sendAct: (a) => sendAct(a),
      requestHands: () => sendReq(null),
      sendHand: (to, attest) => sendGive(attest, to),
    },
    off: () => {
      sendSnap.off();
      sendPriv.off();
      sendAct.off();
      sendReq.off();
      sendGive.off();
    },
  };
}

// ── a hearing ─────────────────────────────────────────────────────────────────────────────────

interface StartArgs {
  seed: number;
  round: number;
  specs: SeatSpec[];
  isHost: boolean;
  solo: boolean;
  onDone: () => void;
}

function startRound(args: StartArgs): void {
  clearScreen();
  setPlaying(true);
  const selfId = args.solo ? 'me' : net!.selfId;

  if (!args.solo) {
    unwire?.();
    const wired = wireRound(net!);
    unwire = wired.off;
    session = new Session({
      selfId,
      solo: false,
      transport: wired.transport,
      roster: args.specs.map((s) => s.id),
      modeId: mode,
      round: args.round,
      standing,
      onPublic: onPublic,
      onPrivate: onPrivate,
      onFlash: (m) => setFlash(m),
      onStranded: () => {
        rounds?.finish();
        showLobby();
      },
      onRoundOver: (aw) => showResults(aw, args),
    });
  } else {
    session = new Session({
      selfId,
      solo: true,
      botTier: 'counsel',
      roster: args.specs.map((s) => s.id),
      modeId: mode,
      round: args.round,
      standing,
      onPublic: onPublic,
      onPrivate: onPrivate,
      onRoundOver: (aw) => showResults(aw, args),
    });
  }

  view = new View({
    onArm: (a) => {
      armed = a;
      play('tap');
      repaint();
    },
    onCommit: () => commit(),
    onBack: (on) => {
      session?.act({ t: 'back', on });
      play('tap');
    },
    onCard: (rank) => openPanel(app, cardHtml(rank)),
    onHelp: () => openPanel(app, HOW_HTML),
    onQuit: () => {
      // Back to the lobby, never out of the room — and finish() FIRST, because the engine lobby
      // paints nothing at all while the round is still 'playing' and this seat is seated.
      if (net) {
        rounds?.finish();
        showLobby();
      } else showMenu();
    },
  });

  const holder = document.createElement('div');
  holder.className = 'countwrap';
  app.append(holder);
  countdown = runCountdown({
    container: holder,
    sfx: play,
    onDone: () => {
      holder.remove();
      app.append(view!.root);
      document.addEventListener('keydown', onKey);
      if (args.isHost) session!.startAsHost(args.seed, args.specs);
      else session!.start();
      if (!args.solo) syncAuthority();
      repaint();
    },
  });
}

function onPublic(pub: PublicState): void {
  latestPub = pub;
  hear(pub);
  repaint();
}
function onPrivate(priv: PrivateView | null): void {
  latestPriv = priv;
  repaint();
}

/** Sound from the public stream, so a guest hears exactly what the host hears. */
function hear(pub: PublicState): void {
  for (const e of pub.events) {
    const at = e.t === 'play' || e.t === 'out' || e.t === 'noeffect' ? e.ply : 0;
    if (at <= seenPly) continue;
    seenPly = at;
  }
  const recent = pub.events.slice(-3);
  for (const e of recent) {
    if (e.t === 'call') play(e.hit ? 'hit' : 'miss');
    else if (e.t === 'out') play('hit');
    else if (e.t === 'shield' && e.on) play('shield');
    else if (e.t === 'swap' || e.t === 'redraw') play('swap');
  }
}

function setFlash(msg: string): void {
  flash = msg;
  flashAt = Date.now();
  repaint();
  setTimeout(() => {
    if (Date.now() - flashAt >= 3000) {
      flash = '';
      repaint();
    }
  }, 3200);
}

function commit(): void {
  const s = session;
  if (!s || !latestPub || !latestPriv) return;
  const { card, target, named } = armed;
  if (!card) {
    play('nope');
    return;
  }
  s.act({ t: 'play', cardId: card.id, target, named });
  armed = { card: null, target: null, named: null };
  play('call');
  repaint();
}

function repaint(): void {
  if (!view || !latestPub || !session) return;
  view.render({ pub: latestPub, priv: latestPriv, me: session.selfId, armed, flash });
}

// ── the end ───────────────────────────────────────────────────────────────────────────────────

function showResults(aw: Award[], args: StartArgs): void {
  play('over');
  setPlaying(false);
  const pub = latestPub;
  const me = session?.selfId ?? 'me';
  if (!pub) return;
  for (const a of aw) standing[a.id] = (standing[a.id] ?? 0) + a.gained;
  const withStanding: PublicState = { ...pub, standing: { ...standing } };
  const target = withStanding.target;
  const done = Math.max(...Object.values(standing)) >= target;
  if (aw.find((a) => a.id === me)?.won) {
    store.set(`won:${mode}`, (store.get<number>(`won:${mode}`, 0) ?? 0) + 1);
  }

  teardownGame();
  app.innerHTML = summaryHtml(withStanding, aw, me, '');
  if (done) {
    app.querySelector('.results')!.insertAdjacentHTML('afterbegin', matchOverHtml(withStanding, me));
  }

  const again = app.querySelector<HTMLButtonElement>('#againBtn')!;
  const toLobby = app.querySelector<HTMLButtonElement>('#lobbyBtn')!;
  const wait = app.querySelector<HTMLElement>('#resWait')!;
  again.textContent = done ? 'Play again' : 'Next hearing';

  if (net && rounds) {
    rounds.finish();
    toLobby.hidden = false;
    toLobby.addEventListener('click', () => showLobby());
    again.addEventListener('click', () => {
      if (done) standing = {};
      rounds!.vote();
      again.disabled = true;
      again.textContent = 'Waiting for the others…';
    });
    // A visible reason and a horizon. One player still reading the table must not hold the room.
    const ticker = setInterval(() => {
      const st = rounds?.state();
      if (!st || !document.body.contains(wait)) {
        clearInterval(ticker);
        return;
      }
      wait.hidden = false;
      wait.textContent =
        st.startsInMs !== null
          ? `Next hearing in ${Math.ceil(st.startsInMs / 1000)}s…`
          : `${st.votes.length} of ${st.present.length} ready.`;
    }, 400);
  } else {
    again.addEventListener('click', () => {
      play('tap');
      if (done) startSolo();
      else args.onDone();
    });
  }
  app.querySelector('#menuBtn')!.addEventListener('click', () => (net ? void leaveRoom() : showMenu()));
  app
    .querySelector('.results-feedback')!
    .addEventListener('click', (e) => window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }));
}

// ── boot ──────────────────────────────────────────────────────────────────────────────────────

async function boot(): Promise<void> {
  hardenViewport();
  // ONCE, before any mesh exists. Trystero builds one global connection pool from the first room's
  // config, so a later call leaves the initiating half of every pair STUN-only.
  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fails open — TURN is an upgrade, never a dependency */
  }

  // A `?room=` is honoured ONCE. After that, "play with friends" always offers create-or-join, so a
  // reload can never drag somebody back into a room they have left.
  const deep = new URL(location.href).searchParams.get('room');
  if (deep && !deepLinkUsed) {
    deepLinkUsed = true;
    roomCode = normalizeRoomCode(deep);
    iMintedIt = false;
    void enterRoom();
    return;
  }
  showMenu();
}

window.addEventListener('beforeunload', () => void net?.leave());
void boot();

// Exposed for the browser verification pass: drives a hearing synchronously in a tab where rAF and
// real timers are throttled. Never used by the game itself.
(window as unknown as Record<string, unknown>).__hearsay = {
  state: () =>
    latestPub && {
      mode: latestPub.modeId,
      round: latestPub.round,
      ply: latestPub.ply,
      turn: latestPub.turn,
      me: session?.selfId,
      phase: latestPub.phase,
      hand: latestPriv?.hand.map((c) => c.rank),
      alive: latestPub.seats.filter((s) => s.alive).length,
      standing: latestPub.standing,
      hosting: session?.hosting(),
    },
  arm: (a: Armed) => {
    armed = a;
    repaint();
  },
  commit: () => commit(),
  /**
   * Start a solo hearing at an arbitrary seat count, so the verification pass can look at SIX seats
   * at 375px — the layout that actually breaks, and the one a three-bot solo game never reaches.
   */
  play: (m: string, seats: number) => {
    mode = modeOf(m).id;
    showMenu();
    startSolo(seats);
    return mode;
  },
  /** Play the whole hearing by taking the shipped bot's move for every seat, including mine. */
  auto: (n = 60) => {
    for (let k = 0; k < n; k++) {
      const s = session;
      if (!s || !latestPub || latestPub.phase !== 'play') break;
      if (!s.hosting()) break;
      if (!s.playFor(latestPub.turn, 'counsel')) break;
    }
    return latestPub?.phase;
  },
  modes: MODE_IDS,
};
